import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);

import {
  ActiveUser,
  BFXNotificationSubType,
  BFXNotificationType,
  BfxBlotterAction,
  CcyLookupService,
  CcyPairLookupService,
  NotificationService,
  OrderDataActionPromiseService,
  OrderDataPrimaryLookupService,
} from "shared/cubiq-services";

import { BfxBlotterActionEvent } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxBlotterOrdersActionSubject } from "app/bfx-blotter-orders/bfx-blotter-orders-action-subject.module";
import { BfxErrorHandlingService } from "app/bfx-common/services/bfx-error-handling.service";
import {
  CALLOUT_TYPE,
  EMCalloutModel,
  IEMActionCallout,
} from "app/bfx-element-library/models/CalloutModel";
import {
  ExecutionMonitoringCmd,
  ExecutionMonitoringCmdPayload,
} from "app/bfx-execution-monitor/models/ExecutionMonitoringCmdPayload";
import { OrderExecutionModel } from "app/bfx-execution-monitor/models/OrderExecutionModel";
import { BfxExecutionMonitorContextMenuService } from "app/bfx-execution-monitor/services/bfx-execution-monitor-context-menu.service";
import { OEDataModel } from "app/order-entry/data/models/oe-data.model";
import { OEModelGetterService } from "app/order-entry/services/model/oe-model-getter.service";
import { ZERO } from "shared/base-components/number-spinner.component";
import { IOrderEntryEvent } from "shared/cubiq-services/model/core/IOrderEntryEvent";
import { ActiveUserProfilePrimaryService } from "shared/auth-services/active-user/ActiveUserProfileService";
import { OEModelOperationsService } from "app/order-entry/services/model/oe-model-operations.service";

const IGNORE_PAUSE_RESUME_ERRORS: Set<CubiqTS.FxLifeCycleEventType> = new Set([
  CubiqTS.FxLifeCycleEventType.TI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.SI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.LOCKED,
  CubiqTS.FxLifeCycleEventType.UNLOCKED,
]);

const IGNORE_EVENT_ERRORS: Set<CubiqTS.FxLifeCycleEventType> = new Set([
  CubiqTS.FxLifeCycleEventType.TI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.SI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.LOCKED,
  CubiqTS.FxLifeCycleEventType.PAUSED,
  CubiqTS.FxLifeCycleEventType.RESUMED,
  CubiqTS.FxLifeCycleEventType.UNLOCKED,
  CubiqTS.FxLifeCycleEventType.CANCEL_PENDING_FWDS,
]);

const CLEAR_EVENT_ERRORS: Set<CubiqTS.FxLifeCycleEventType> = new Set([
  CubiqTS.FxLifeCycleEventType.TI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.SI_REQUIRED,
  CubiqTS.FxLifeCycleEventType.CANCEL_PENDING_FWDS,
]);
/**
 * This services acts a mediator/controller for all order execution monitor components.
 * Each displayed component subscribes for a single model and all updates to this model are managed
 * within this service and communivcated back to the component on an event stream.
 */
@Injectable()
export class BfxExecutionMonitorService {
  private subscribedOrders: Map<string, OrderExecutionModel>;

  private interfaceCmdEventSource = new Subject<ExecutionMonitoringCmdPayload>();
  public interfaceCmd$: Observable<ExecutionMonitoringCmdPayload> =
    this.interfaceCmdEventSource.asObservable();

  /** Promise that resolved when all services initialised */
  private initialized: Promise<void>;

  /** Current user id used for filter error messages */
  private activeUserCd: string;

  constructor(
    private translate: TranslateService,
    private ccyPairService: CcyPairLookupService,
    private ccyLookupService: CcyLookupService,
    private orderService: OrderDataPrimaryLookupService,
    private menuService: BfxExecutionMonitorContextMenuService,
    private orderActionService: OrderDataActionPromiseService,
    private actionSubject: BfxBlotterOrdersActionSubject,
    private errorHandler: BfxErrorHandlingService,
    private getter: OEModelGetterService,
    private notifService: NotificationService,
    private modelOperations: OEModelOperationsService,
    activeUserService: ActiveUserProfilePrimaryService,
  ) {
    this.initializeSubscriptions();
    activeUserService
      .getActiveUserData()
      .then((user: ActiveUser) => (this.activeUserCd = user.userCode));
  }

  /**
   * Need to listen to all order lifecycle events
   */
  private initializeSubscriptions(): void {
    this.subscribedOrders = new Map();

    this.initialized = Promise.all([
      this.menuService.initialised,
      this.orderService.isPrimed(),
    ]).then(() => {
      this.orderService.getModelUpdateSubject().subscribe((dataItem) => {
        this.processOrderUpdate(dataItem as CubiqTS.OrderDataImpl);
      });
      this.orderService.getModelOrderEntrySubject().subscribe((dataItem) => {
        this.processOrderEntryUpdate(dataItem);
      });
      this.orderService.getModelDeleteSubject().subscribe((orderId) => {
        this.processOrderDeletion(orderId);
      });
    });
  }

  /**
   * Process changes to the specified order and emit changes on the $interfaceCmd observable
   * @param orderId
   */
  public subscribeOrder(orderId: string): Promise<OrderExecutionModel> {
    // wrap in a promise so will not complete until menu service is initialised
    return this.initialized.then(() => {
      let model = this.subscribedOrders.get(orderId);
      if (!model) {
        return this.getOrderExecutionModel(orderId).then((model) => {
          this.subscribedOrders.set(orderId, model);
          model.addSubscriber();
          return model;
        });
      } else {
        model.addSubscriber();
        return Promise.resolve(model);
      }
    });
  }

  /**
   * Process changes to the specified order and emit changes on the $interfaceCmd observable
   * @param orderId
   */
  public getOrderExecutionModel(orderId: string): Promise<OrderExecutionModel> {
    return new Promise((res) => {
      let model = this.subscribedOrders.get(orderId);
      if (!model) {
        model = new OrderExecutionModel(
          orderId,
          this.ccyPairService,
          this.ccyLookupService,
          this.translate,
          this.menuService,
        );
        // If an existing order is there then apply it but don't want for promise as may
        // be out of date
        this.orderService.getItem(orderId).then((orderData) => {
          let cloneOrder: CubiqTS.OrderDataImpl;

          if (orderData == null) {
            // Nothing in our data service - order could be a filled leg or a future leg

            // FInd an active leg in our Orders list that we can use to populate our order from
            const refId = this.getter.findRefIdForOrderId(orderId);
            if (refId) {
              // We have a BINGO

              // As OEDataModel does notr have all data we need, try to find an active leg of this strategy with
              // populated fields that we use as template
              const refIds = this.getter.getStrategyNodesListRoot(refId);
              for (const nodeRefId of refIds) {
                const orderId = this.getter.getOrderId(nodeRefId);
                if (orderId) {
                  cloneOrder = this.orderService.getBase().getOrderData(orderId);
                  if (cloneOrder) {
                    break;
                  }
                }
              }
              if (!cloneOrder) {
                // Nothing we can use as template so create a fresh one
                cloneOrder = new CubiqTS.OrderDataImpl();
                cloneOrder.entryUserId = new CubiqTS.Long(-1);
              }

              const oeModel = this.getter.get(refId);
              this.populateOrderFromOEDataModel(cloneOrder, oeModel);
              this.updateModelOrder(model, cloneOrder, false);
            }
          } else {
            cloneOrder = new CubiqTS.OrderDataImpl();
            // Ensure there are no initial errors displayed in any callouts
            cloneOrder.copyFrom(orderData);
            cloneOrder.orderErrors = null;

            this.updateModelOrder(model, cloneOrder, true);
            this.modelOperations.processExternalTransfer(cloneOrder);
          }
          res(model);
        });
      } else {
        res(model);
      }
    });
  }

  /**
   * Populate the OrderData object from the data stored in the OEDataModel - for Filled/Pending orders we don't
   * have these in memory but when editing strategies, we do have OEDAtaModels. The EM only requires a small subset of data to display
   * and the OEDataModel has enough so no need to go to the server
   * @param orderData
   * @param model
   */
  private populateOrderFromOEDataModel(orderData: CubiqTS.OrderDataImpl, model: OEDataModel): void {
    orderData.orderId = CubiqTS.Long.fromString(model.orderId);
    orderData.orderRef = model.orderRef;
    orderData.baseCode = model.ccyPair.substring(0, 3);
    orderData.nonBaseCode = model.ccyPair.substring(3);
    orderData.isInvertedOrderFg = orderData.nonBaseCode === model.ccyPairModel.base;
    orderData.executedQty = model.executedQty ?? ZERO;
    orderData.vwapAllInRt = model.vwapAllInRt;
    orderData.fixedCode = model.fixedCcy.ccy;
    orderData.orderQty = model.orderAmount ?? ZERO;
    orderData.leavesQty = orderData.orderQty.minus(orderData.executedQty);
    orderData.orderState = model.srvOrderState;
    orderData.orderErrors = null;
  }

  /**
   * Remove subscription for the order - if no other subscriptions made, then stop tracking order changes
   * @param orderId
   */
  public unsubscribeOrder(orderId: string) {
    let model = this.subscribedOrders.get(orderId);
    if (model) {
      model.removeSubscriber();
      if (model.getSubscriberCount() == 0) {
        this.subscribedOrders.delete(orderId);
      }
    }
  }

  /**
   * Handle the update of an locally-visble order - we ignore flashing updates
   * @param order
   * @returns
   */
  private processOrderUpdate(order: CubiqTS.OrderDataImpl): void {
    const orderId = order.orderId.toString();

    // logger.out.info(`em-s: Order UPDATE ${orderId}`);
    const model = this.subscribedOrders.get(orderId);
    if (model) {
      logger.out.info(`em-s: found order subscription for ${orderId}`);
      if (order["uiFlashEvent"] !== true) {
        // We ignore ui flashing event - this is a duplicate of the previous event
        this.updateModelOrder(model, order, true);
      }
    }
  }

  /**
   * Update the model with a new Order, checking errors and operation states
   * @param model
   * @param order
   * @param locallyMonitored Is order monitored in the UI?
   */
  private updateModelOrder(
    model: OrderExecutionModel,
    order: CubiqTS.OrderDataImpl,
    locallyMonitored: boolean,
  ) {
    const oldState = model.orderState;
    const newCalloutDisabled = oldState == CubiqTS.OrderState.REJECTED;

    model.setOrder(order, locallyMonitored);

    if (!newCalloutDisabled && this.addCalloutFromOrder(model, order)) {
      this.notifyCalloutChange(model);
    }
    logger.out.warn(`em-s - setting order state to ${model.orderStateStr}`);
    this.notifyUpdate(model);

    if (model.pauseResumeOperationActive) {
      // If we're in process of pause op, then check to see if complete
      if (
        (model.pauseOperation === CubiqTS.PauseResumeAction.PAUSE &&
          order.pauseState === CubiqTS.PauseState.PAUSED) ||
        (model.pauseOperation === CubiqTS.PauseResumeAction.RESUME &&
          order.pauseState === CubiqTS.PauseState.NOT_PAUSED)
      ) {
        // mark it as succeeded
        model.setPauseResumeOperationActive(false);
        const actionCallout = this.createCalloutModel(
          order.pauseState,
          model.pauseOperation === CubiqTS.PauseResumeAction.PAUSE,
        );
        model.setActionCallout(actionCallout);
        this.notifyCalloutChange(model);
      } else if (
        (model.pauseOperation === CubiqTS.PauseResumeAction.PAUSE &&
          order.pauseState === CubiqTS.PauseState.PAUSE_PENDING) ||
        (model.pauseOperation === CubiqTS.PauseResumeAction.RESUME &&
          order.pauseState === CubiqTS.PauseState.RESUME_PENDING)
      ) {
        // WE are waiting for concrete result
      } else {
        const ignoreErrors = IGNORE_PAUSE_RESUME_ERRORS.has(order["evtTypeId"]);

        if (!ignoreErrors && order.orderErrors?.errorMessages?.length > 0) {
          model.setPauseResumeOperationActive(false);
          this.addCalloutFromErrors(model, order.orderErrors);
          this.notifyCalloutChange(model);
        }
      }
    }
  }

  /**
   * Handle the deletion of an order - on OrderEntry events, we will know the state so we can update the state in the display
   * without deleting the model from our view
   * @param event
   * @returns
   */
  private processOrderEntryUpdate(event: IOrderEntryEvent): void {
    const orderId = event?.orderId;
    if (orderId) {
      logger.out.info(`em-s: Order Entry update ${orderId}`);
      const model = this.subscribedOrders.get(orderId);

      if (model) {
        // Lock events don't have any errors and any on order are probably stale so we clear those
        const ignoreErrors = IGNORE_EVENT_ERRORS.has(event.evtType);
        const clearErrors = CLEAR_EVENT_ERRORS.has(event.evtType);

        if (clearErrors) {
          // We have an event that clear all previous errors
          if (model.hasActionCallout()) {
            model.setActionCallout(null);
            this.notifyCalloutChange(model);
          }
        } else if (!ignoreErrors) {
          if (event.orderErrors && !event.rollbackEventFg) {
            // ON rollback, we keep previous errors, otherwise we show new errors or clear
            const reasons = this.getFilteredErrorMessages(event.orderErrors);
            if (this.addCalloutFromErrorsAndState(model, reasons, event.state)) {
              this.notifyCalloutChange(model);
            }
          } else if (!event.rollbackEventFg) {
            model.setActionCallout(null);
            this.notifyCalloutChange(model);
          }
        }

        let isTerminal: boolean;
        switch (event.state) {
          case CubiqTS.OrderState.EXPIRED:
          case CubiqTS.OrderState.CANCELLED:
          case CubiqTS.OrderState.DELETED:
          case CubiqTS.OrderState.FILLED:
          case CubiqTS.OrderState.REJECTED:
            isTerminal = true;
            break;
          default:
            isTerminal = false;
            break;
        }
        if (isTerminal) {
          logger.out.info(`em-s: orderEntryUpdate() - found order subscription for ${orderId}`);
          model.updateFromTerminalData(event);
          this.notifyUpdate(model);
        }
        if (event.rollbackEventFg) {
          logger.out.warn(
            `em-s -Got rollback for order ${model.orderId} - restoring original form data`,
          );
          // this.reloadOrder(model);
        }
      }
    }
  }

  /**
   * Handle the deletion of an order - on OrderEntry events, we will know the state so we can update the state in the display
   * without deleting the model from our view
   * @param event
   * @returns
   */
  private processOrderDeletion(orderId: string): void {
    if (orderId) {
      logger.out.info(`em-s: Order deletion ${orderId}`);
      const model = this.subscribedOrders.get(orderId);

      if (model) {
        // do {
        //   model.removeSubscriber();
        // } while (model.getSubscriberCount() > 0);
        // this.subscribedOrders.delete(orderId);
        model.setActionCallout(
          new EMCalloutModel(
            "negative",
            this.translate.instant("execution_monitor.visibility_lost"),
          ),
        );
        const payload = new ExecutionMonitoringCmdPayload(
          ExecutionMonitoringCmd.LOST_VISIBILITY,
          model.orderId,
          model,
        );
        this.interfaceCmdEventSource.next(payload);
      }
    }
  }

  private notifyUpdate(model: OrderExecutionModel) {
    const payload = new ExecutionMonitoringCmdPayload(
      ExecutionMonitoringCmd.UPDATE_MODEL,
      model.orderId,
      model,
    );
    this.interfaceCmdEventSource.next(payload);
  }

  private notifyCalloutChange(model: OrderExecutionModel) {
    const payload = new ExecutionMonitoringCmdPayload(
      ExecutionMonitoringCmd.ACTION_RESPONSE,
      model.orderId,
      model,
    );
    this.interfaceCmdEventSource.next(payload);
  }

  public showDetails(model: OrderExecutionModel) {
    // NB I need to figure out why the OrderDetails needs a reference to a LadderBlotter...
    const menuId = "OrderDetails_" + model.orderId;
    const blotterKey: any = { blotterMenuId: menuId, blotterId: menuId };

    let actionEvent = new BfxBlotterActionEvent(
      BfxBlotterAction.ACTION_TRADE_VIEW_DETAILS,
      blotterKey,
      [model.orderId],
      [model.order],
    );
    this.actionSubject.next(actionEvent);
  }
  public cancel(model: OrderExecutionModel) {
    let actionEvent = new BfxBlotterActionEvent(
      BfxBlotterAction.ACTION_ORDER_CANCEL,
      null,
      [model.orderId],
      [model.order],
    );
    this.actionSubject.next(actionEvent);
  }
  public pause(model: OrderExecutionModel) {
    model.setActionCallout(new EMCalloutModel("neutral", "Pause operation requested"));
    this.notifyCalloutChange(model);
    this.pauseResumeOrder(model, CubiqTS.PauseResumeAction.PAUSE);
  }

  public resume(model: OrderExecutionModel) {
    model.setActionCallout(new EMCalloutModel("neutral", "Resume operation requested"));
    this.notifyCalloutChange(model);
    this.pauseResumeOrder(model, CubiqTS.PauseResumeAction.RESUME);
  }

  public modify(model: OrderExecutionModel) {
    let actionEvent = new BfxBlotterActionEvent(
      BfxBlotterAction.ACTION_ORDER_MODIFY,
      null,
      [model.orderId],
      [model.order],
    );
    this.actionSubject.next(actionEvent);
  }

  public reloadOrder(model: OrderExecutionModel) {
    let actionEvent = new BfxBlotterActionEvent(
      BfxBlotterAction.ACTION_ORDER_VIEW_TICKET,
      null,
      [model.orderId],
      [model.order],
    );
    this.actionSubject.next(actionEvent);
  }

  public fillNow(model: OrderExecutionModel) {
    if (model.hasActionCallout()) {
      model.setActionCallout(null);
      this.notifyCalloutChange(model);
    }

    let actionEvent = new BfxBlotterActionEvent(
      BfxBlotterAction.ACTION_ORDER_FILL_NOW,
      null,
      [model.orderId],
      [model.order],
    );
    this.actionSubject.next(actionEvent);
  }

  /**
   * Send off a Pause/Resume request and process the response
   * @param model
   * @param action  Either pause or resume
   */
  private pauseResumeOrder(model: OrderExecutionModel, action: CubiqTS.PauseResumeAction) {
    let isPause: boolean = action == CubiqTS.PauseResumeAction.PAUSE;

    let request: CubiqTS.OrderPauseResumeRequestImpl = new CubiqTS.OrderPauseResumeRequestImpl();
    request.orderIds = [model.orderId];
    request.reason = "One-Click UI Pause/Resume";
    request.action = action;

    if (model.hasActionCallout()) {
      model.setActionCallout(null);
      this.notifyCalloutChange(model);
    }

    // disable pause/resume buttons
    model.setPauseResumeOperationActive(true);
    model.pauseOperation = action;
    this.notifyUpdate(model);

    this.orderActionService.pauseResumeOrders(request).then(
      (response: CubiqTS.StandardReportImpl) => {
        if (response.encodableType == CubiqTS.ErrorsImpl.enType) {
          this.handleErrorResult(model, response, isPause);
          // Ensure Pause/resume can be clicked again
          model.setPauseResumeOperationActive(false);
        } else if (response.results) {
          let results: Array<CubiqTS.OrderPauseResumeResponseImpl> = response.results;
          let pauseState: CubiqTS.PauseState = null;
          let actionCallout: IEMActionCallout;

          if (model.pauseResumeOperationActive) {
            // Only want to update callouts if we haven't already updated them from
            // lifecycle events
            if (results.length == 1) {
              const result = results[0];
              pauseState = result.state;
              // Are there errors?
              const reasons = this.errorHandler.getErrorMessages(result.errors);
              if (reasons.length > 0) {
                actionCallout = new EMCalloutModel("warning", reasons.join("\n"));
                actionCallout.isEMResult = true;
              }
            }
            if (!actionCallout) {
              actionCallout = this.createCalloutModel(pauseState, isPause);
            }

            model.setActionCallout(actionCallout);
          }
        }
        this.notifyCalloutChange(model);
        this.notifyUpdate(model);
      },
      // Reject
      (error) => {
        logger.out.warn(`em-s: Pause/resume error for ${model.orderId}: ${error?.toString()}`);
        // Ensure Pause/resume can be clicked again
        model.setPauseResumeOperationActive(false);
        this.notifyUpdate(model);
      },
    );
  }

  /**
   * Process a full fast fail from the pause/resume request
   * @param model
   * @param response
   */
  private handleErrorResult(
    model: OrderExecutionModel,
    response: CubiqTS.StandardReportImpl,
    isPause,
  ) {
    let errors = new CubiqTS.ErrorsImpl();
    Object.assign(errors, response);

    const subType = isPause
      ? BFXNotificationSubType.PAUSE_FAIL
      : BFXNotificationSubType.RESUME_FAIL;
    this.notifService.handleErrors(response, BFXNotificationType.ORDER_ACTION_FAIL, subType);
  }

  private addCalloutFromErrors(
    model: OrderExecutionModel,
    errors: CubiqTS.ErrorsImpl,
  ): IEMActionCallout {
    const reasons = this.getFilteredErrorMessages(errors);
    const reasonStr = reasons.length > 0 ? reasons.join("\n") : "No error reason found";
    const retCalloutModel = new EMCalloutModel("warning", "Operation failed: " + reasonStr);
    retCalloutModel.isEMResult = true;
    model.setActionCallout(retCalloutModel);
    return retCalloutModel;
  }

  /**
   * Check the event that caused the OrderUpdate to see if it includes any event type that should not be
   * ignored - if the event if not on ignore list then process orderErrors if not empty
   * @param model
   * @param order
   * @returns
   */
  private addCalloutFromOrder(model: OrderExecutionModel, order: CubiqTS.OrderDataImpl): boolean {
    const ignoreErrors = IGNORE_EVENT_ERRORS.has(order["evtTypeId"]);
    if (!ignoreErrors) {
      // On any successful Lock event we want to ensure any prior errors are removed
      let errors = ignoreErrors ? null : order.orderErrors;
      const reasons = this.getFilteredErrorMessages(errors);
      return this.addCalloutFromErrorsAndState(model, reasons, order.orderState);
    }
    return false;
  }

  /**
   * Get rid of any error messages we don't want to show
   * @param errors
   * @returns
   */
  private getFilteredErrorMessages(errors: CubiqTS.ErrorsImpl): string[] {
    if (errors?.errorMessages?.length > 0) {
      let orderErrors = Object.assign(new CubiqTS.ErrorsImpl(), errors);
      // Duplicate all errorMessage except those that fail the filter
      orderErrors.errorMessages = errors.errorMessages.filter((msg: any) => {
        const message = JSON.parse(msg as any);
        switch (message.id) {
          case "user.abort":
            return false;
          case "order.rejected.byTrader":
            return message.args[0] !== this.activeUserCd;
          default:
            return true;
        }
      });
      return this.errorHandler.getErrorMessages(orderErrors);
    }
    return [];
  }

  private addCalloutFromErrorsAndState(
    model: OrderExecutionModel,
    reasons: string[],
    orderState: CubiqTS.OrderState,
  ): boolean {
    let retValue: boolean;
    if (reasons.length > 0) {
      let calloutType: CALLOUT_TYPE;
      switch (orderState) {
        case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
          calloutType = "neutral";
          break;
        default:
          calloutType = "warning";
          break;
      }
      retValue = model.setActionCallout(new EMCalloutModel(calloutType, reasons.join(",")));
    } else {
      retValue = model.setActionCallout(null);
    }
    return retValue;
  }

  /**
   * Create the model used for user feedback in a callout checking the current pause state against the expected state
   * @param pauseState
   * @param isPauseOperation If not defined, then all pause/resume operations are treated as successful
   * @returns
   */
  private createCalloutModel(
    pauseState: CubiqTS.PauseState,
    isPauseOperation?: boolean,
  ): IEMActionCallout {
    let resultStatus: string = "";
    let success = false;

    if (typeof pauseState !== "number") {
      success = false;
      resultStatus = "Unknown failure reason";
    } else {
      switch (pauseState) {
        case CubiqTS.PauseState.PAUSED: {
          success = isPauseOperation !== false;
          resultStatus =
            isPauseOperation !== false
              ? this.translate.instant("order.pause.action.success")
              : this.translate.instant("order.resume.action.failed");
          break;
        }
        case CubiqTS.PauseState.NOT_PAUSED: {
          success = isPauseOperation !== true;
          resultStatus =
            isPauseOperation !== true
              ? this.translate.instant("order.resume.action.success")
              : this.translate.instant("order.pause.action.fail");
          break;
        }
        case CubiqTS.PauseState.PAUSE_PENDING: {
          success = true;
          resultStatus = this.translate.instant("order.state.PAUSE_PENDING");
          break;
        }
        case CubiqTS.PauseState.RESUME_PENDING: {
          success = true;
          resultStatus = this.translate.instant("order.state.RESUME_PENDING");
          break;
        }
      }
    }

    const calloutData: IEMActionCallout = new EMCalloutModel(
      success ? "positive" : "warning",
      resultStatus,
    );
    calloutData.isEMResult = true;

    return calloutData;
  }
}
